import {
  cancelService,
  getCancellationFormOptions,
  restoreService,
} from "@/utils/serviceActionsHelper";
import { clear, clearListCache, createKey, get } from "@/utils/networkCache";
import { SET_CHUNK, SET_ITEM, SET_LIST, SET_PAGINATION, SET_SORT } from "@/store/commonMutations";
import { request } from "leatherman-js";
import Status from "@/classes/Status";
import Vue from "vue";

const CACHE_ITEM_KEY = "plan";
const CACHE_LIST_KEY = "plans";
const CACHE_PRIMARY_SITE_KEY = "primarySite";

const store = {
  namespaced: true,
  state: {
    chunk: null,
    item: null,
    itemStatus: new Status(),
    primarySite: null,
    primarySiteStatus: new Status(),
    list: [],
    pagination: null,
    sort: null,
    status: new Status(),
  },
  mutations: {
    SET_CHUNK,
    SET_ITEM,
    SET_LIST,
    SET_PAGINATION,
    SET_SORT,
    SET_ITEM_STATUS(state, payload) {
      state.itemStatus.value = payload;
    },
    SET_LIST_STATUS(state, payload) {
      state.status.value = payload;
    },
    SET_LIST_ITEM(state, payload) {
      const index = state.list.findIndex((item) => item.id === payload.id);
      if (index >= 0) {
        Vue.set(state.list, index, payload);
      }
    },
    SET_PRIMARY_SITE(state, payload) {
      state.primarySite = payload;
    },
    SET_PRIMARY_SITE_STATUS(state, payload) {
      state.primarySiteStatus.value = payload;
    },
    UPDATE_PRIMARY_SITE(state, payload) {
      state.primarySite.value = payload;
    },
    SET_CANCELLATION_FORM_OPTIONS(state, payload) {
      state.cancellationFormOptions = payload;
    },
  },
  actions: {
    async getItem({ commit, state }, id) {
      const newStatus =
        (state.itemStatus.isLoaded || state.itemStatus.isUpdating) && id === state.item?.id
          ? Status.UPDATING
          : Status.LOADING;
      commit("SET_ITEM_STATUS", newStatus);
      try {
        const response = await get(
          createKey(CACHE_ITEM_KEY, { id: Number(id) }),
          request.makeParse,
          ["getPlan", { id }]
        );
        commit("SET_ITEM", response);
        commit("SET_ITEM_STATUS", Status.LOADED);
        return response;
      } catch (e) {
        commit("SET_ITEM_STATUS", Status.ERROR);
      }
    },
    async getPrimarySite({ commit, state }, siteId) {
      const newStatus =
        (state.primarySiteStatus.isLoaded || state.primarySiteStatus.isUpdating) &&
        siteId === state.item?.id
          ? Status.UPDATING
          : Status.LOADING;

      commit("SET_PRIMARY_SITE_STATUS", newStatus);
      try {
        const response = await get(
          createKey(CACHE_PRIMARY_SITE_KEY, { id: siteId }),
          request.makeParse,
          ["getSite", { id: siteId }]
        );
        commit("SET_PRIMARY_SITE", response);
        commit("SET_PRIMARY_SITE_STATUS", Status.LOADED);
        return response;
      } catch (e) {
        commit("SET_PRIMARY_SITE_STATUS", Status.ERROR);
      }
    },
    async getList({ commit }, params) {
      commit("SET_LIST_STATUS", Status.LOADING);
      commit("SET_CHUNK", params);
      commit("SET_SORT", params);
      try {
        const response = await get(createKey(CACHE_LIST_KEY, params), request.makeParse, [
          "getPlans",
          params,
        ]);
        commit("SET_LIST", response.list);
        commit("SET_PAGINATION", response.pagination);
        commit("SET_LIST_STATUS", Status.LOADED);
        return response;
      } catch (e) {
        commit("SET_LIST_STATUS", Status.ERROR);
      }
    },
    // TODO: calculateUpgradeTax needs to be consolidated with the similar action in the checkout store
    // thinking of adding a tax store
    // tax may or may not be calculated by different methods for different products (Cloud Servers vs Plans vs Sites vs Upgrades) in the future.
    calculateUpgradeTax(context, { planId, packageId, term, addonIds }) {
      return request.makeParse(
        "getSalesTax",
        {},
        {
          packageId,
          term,
          addonIds,
          planId,
        }
      );
    },
    async getUpgradePrices({ rootState }, { planId, appId, isTShirt, isFreeTrial }) {
      const packages = await request.makeParse("getPlanUpgradePrices", {
        planId,
        appId,
        notTShirt: !isTShirt,
        isFreeTrial: isFreeTrial || false,
      });

      return packages.filter(({ packageId }) => {
        return !rootState.checkout.storeBuilderIds.includes(`${packageId}`);
      });
    },
    async updateName(context, { id, data }) {
      return request.makeParse("updatePlanName", { id }, data);
    },
    async updatePackage(context, { id, data }) {
      return request.makeParse("updatePlanPackage", { id }, data);
    },
    async updateTerm(context, { id, data }) {
      return request.makeParse("updatePlanTerm", { id }, data);
    },
    refreshItem({ dispatch, state }, id) {
      const cacheKey = createKey(CACHE_ITEM_KEY, { id });
      clear(cacheKey);
      if (id === state.item?.id) {
        dispatch("getItem", id);
      }
    },
    refreshList({ dispatch, state }) {
      clearListCache(CACHE_LIST_KEY);
      dispatch("getList", {
        pageIndex: state.chunk?.pagination?.index,
        pageSize: state.chunk?.pagination?.size,
        sortProperty: state.chunk?.sort?.property,
        sortDirection: state.chunk?.sort?.direction,
      });
    },
    refreshListByPlanId({ dispatch, state }, planId) {
      const plan = state.list?.find((item) => item.id === planId);
      if (plan) {
        clearListCache(CACHE_LIST_KEY);
        dispatch("getList", {
          pageIndex: state.chunk?.pagination?.index,
          pageSize: state.chunk?.pagination?.size,
          sortProperty: state.chunk?.sort?.property,
          sortDirection: state.chunk?.sort?.direction,
        });
      }
    },
    refreshPrimarySite({ dispatch, state }, siteId) {
      const cacheKey = createKey(CACHE_PRIMARY_SITE_KEY, { id: siteId });
      clear(cacheKey);
      if (siteId === state.primarySite?.id) {
        dispatch("getPrimarySite", siteId);
      }
    },
    getCancellationFormOptions,
    cancelService,
    restoreService,
  },
  getters: {
    get: (state) => (id) => {
      if (state.item?.id === id) return state.item;
      return state.list.find((item) => item.id === id);
    },
    getItem: (state) => state.item,
    getItemStatus: (state) => state.itemStatus,
    getPrimarySite: (state) => state.primarySite,
    getPrimarySiteStatus: (state) => state.primarySiteStatus,
    getList: (state) => state.list,
    getPagination: (state) => state.pagination,
    getSort: (state) => state.sort,
  },
};

export default store;
